import { element, slot } from "./dom.js";
import type { SavedTag } from "./saved-parts.js";
import type { OwnListChoice } from "./worksheet-sources.js";

/**
 * The two selects under My saved words: which tag, and which part.
 *
 * Their options are the reader's own, so they are drawn once the saved words
 * have arrived, which is after the page has. The tag select lists every tag
 * the words carry, with how many carry it. The part select appears only when
 * the words chosen are too many for one sheet. `sheet-tool.ts` reads both as
 * it reads every other control, by `data-cb`. The line asking a reader to
 * sign in is hidden once their words have arrived.
 */
export class SavedChoices {
  readonly #choices: HTMLElement;
  readonly #tag: HTMLSelectElement;
  readonly #partChoice: HTMLElement;
  readonly #part: HTMLSelectElement;
  readonly #signIn: HTMLElement | undefined;

  constructor(properties: {
    readonly choices: HTMLElement;
    readonly tag: HTMLSelectElement;
    readonly partChoice: HTMLElement;
    readonly part: HTMLSelectElement;
    readonly signIn: HTMLElement | undefined;
  }) {
    this.#choices = properties.choices;
    this.#tag = properties.tag;
    this.#partChoice = properties.partChoice;
    this.#part = properties.part;
    this.#signIn = properties.signIn;
  }

  /** The selects in a generator's container, if the page has them. */
  static on(container: HTMLElement): SavedChoices | undefined {
    const choices = slot(container, "saved-choices");
    const tag = slot(container, "tag");
    const partChoice = slot(container, "part-choice");
    const part = slot(container, "part");

    return choices !== undefined &&
      partChoice !== undefined &&
      tag instanceof HTMLSelectElement &&
      part instanceof HTMLSelectElement
      ? new SavedChoices({
          choices,
          tag,
          partChoice,
          part,
          signIn: slot(container, "saved-sign-in"),
        })
      : undefined;
  }

  /**
   * List the tags, choosing one.
   * @param total - How many words are saved, for the option that takes all.
   */
  tags(tags: readonly SavedTag[], total: number, chosen: string): void {
    this.#tag.replaceChildren(
      element("option", {
        text: `All saved words (${String(total)})`,
        attributes: { value: "" },
      }),
      ...tags.map(({ tag, count }) =>
        element("option", {
          text: `${tag} (${String(count)})`,
          attributes: { value: tag },
        }),
      ),
    );
    this.#tag.value = tags.some((one) => one.tag === chosen) ? chosen : "";
    this.#tag.disabled = tags.length === 0;
    this.#choices.hidden = false;
    if (this.#signIn !== undefined) {
      this.#signIn.hidden = true;
    }
  }

  /**
   * List the parts, choosing one, or hide the select for words that fit.
   * @param parts - How many words each part holds, in order.
   * @param unit - What a part holds, for the options: words, or sentences on
   * the sentence sheet.
   */
  parts(parts: readonly number[], chosen: number, unit = "words"): void {
    let first = 1;
    this.#part.replaceChildren(
      ...parts.map((size, at) => {
        const option = element("option", {
          text: `Part ${String(at + 1)} of ${String(parts.length)}, ${unit} ${String(first)} to ${String(first + size - 1)}`,
          attributes: { value: String(at + 1) },
        });
        first += size;

        return option;
      }),
    );
    this.#part.value = String(chosen);
    this.#partChoice.hidden = parts.length < 2;
  }

  /**
   * Hide the line asking the reader to sign in, for a sheet whose saved
   * sentences have arrived and which has no tags to offer.
   */
  signedIn(): void {
    if (this.#signIn !== undefined) {
      this.#signIn.hidden = true;
    }
  }

  /** Hide both, for a reader the saved words could not be read for. */
  hide(): void {
    this.#choices.hidden = true;
    if (this.#signIn !== undefined) {
      this.#signIn.hidden = false;
    }
  }
}

/**
 * The select under My Lists, of the reader's own Lists and their parts.
 *
 * Drawn once the Lists have arrived, like the tag select above. Each List is
 * an option with its parts after it, so a sheet prints a whole List or one
 * part. The line asking a reader to sign in becomes one saying where Lists
 * are made, for a reader who has none.
 */
export class OwnListSelect {
  readonly #choices: HTMLElement;
  readonly #select: HTMLSelectElement;
  readonly #note: HTMLElement | undefined;
  readonly #none: string;

  constructor(properties: {
    readonly choices: HTMLElement;
    readonly select: HTMLSelectElement;
    readonly note: HTMLElement | undefined;
    /** What the note says to a reader with no List. */
    readonly none: string;
  }) {
    this.#choices = properties.choices;
    this.#select = properties.select;
    this.#note = properties.note;
    this.#none = properties.none;
  }

  /** The select in a generator's container, if the page has one. */
  static on(container: HTMLElement): OwnListSelect | undefined {
    const choices = slot(container, "mine-choices");
    const select = slot(container, "mine");
    const note = slot(container, "mine-note");

    return choices !== undefined && select instanceof HTMLSelectElement
      ? new OwnListSelect({
          choices,
          select,
          note,
          none: note?.dataset["cbNone"] ?? "",
        })
      : undefined;
  }

  /**
   * List the Lists and their parts, choosing one.
   * @returns The path chosen, which is the first List's for none or one the
   * reader no longer has.
   */
  lists(lists: readonly OwnListChoice[], chosen: string): string {
    const options = lists.flatMap((list) => [
      { value: list.id, text: `${list.name} (${String(list.words)})` },
      ...list.parts.map((part) => ({
        value: `${list.id}/${part.id}`,
        text: `${list.name} › ${part.name} (${String(part.words)})`,
      })),
    ]);
    this.#select.replaceChildren(
      ...options.map(({ value, text }) =>
        element("option", { text, attributes: { value } }),
      ),
    );
    const path = options.some((option) => option.value === chosen)
      ? chosen
      : (options[0]?.value ?? "");
    this.#select.value = path;
    this.#choices.hidden = lists.length === 0;
    if (this.#note !== undefined) {
      this.#note.hidden = lists.length > 0;
      if (lists.length === 0) {
        this.#note.textContent = this.#none;
      }
    }

    return path;
  }

  /** Hide the select, for a reader the Lists could not be read for. */
  hide(): void {
    this.#choices.hidden = true;
    if (this.#note !== undefined) {
      this.#note.hidden = false;
    }
  }
}
